import React from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Autocomplete,
    TextField
} from '@mui/material';
import {
    AttachMoney,
    Schedule,
    TrendingUp,
    People
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../../hooks/useRTL';
import { formatCurrency } from '../../../utils/formatters';

const SummaryCards = ({
    analytics,
    analyticsLoading,
    error,
    branches,
    filters,
    setFilters,
    allowedBranches,
    canSelectBranch
}) => {
    const { t } = useTranslation();
    const { isRTL, cardGridProps, iconContainerProps, textContainerProps, containerProps } = useRTL();

    if (analyticsLoading) {
        return (
            <Grid container spacing={3} sx={{ mb: 5, ...containerProps }}>
                <Grid item xs={12}>
                    {error ? (
                        <Alert severity="error">{error}</Alert>
                    ) : (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
                            <CircularProgress />
                        </Box>
                    )}
                </Grid>
            </Grid>
        );
    }

    const summaryData = [
        {
            title: t('dashboard.dailyIncome'),
            value: formatCurrency(analytics.daily.income),
            icon: AttachMoney,
            color: 'primary.main'
        },
        {
            title: t('dashboard.dailyAppointments'),
            value: analytics.daily.appointments,
            icon: Schedule,
            color: 'primary.main'
        },
        {
            title: t('dashboard.weeklyIncome'),
            value: formatCurrency(analytics.weekly.income),
            icon: TrendingUp,
            color: 'primary.main'
        },
        {
            title: t('dashboard.weeklyAppointments'),
            value: analytics.weekly.appointments,
            icon: People,
            color: 'primary.main'
        },
        {
            title: t('dashboard.monthlyAppointments'),
            value: analytics.monthly.appointments,
            icon: People,
            color: 'primary.main'
        },
        {
            title: t('dashboard.monthlyIncome'),
            value: formatCurrency(analytics.monthly.income),
            icon: People,
            color: 'primary.main'
        },
        {
            title: t('dashboard.totalExpenses'),
            value: formatCurrency(analytics.branchTotals.reduce((acc, current) => acc + current.totalExpenses, 0)),
            icon: AttachMoney,
            color: 'error.main'
        }
    ];

    return (
        <>
            {/* Branch Selection */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Autocomplete
                    options={allowedBranches}
                    getOptionLabel={(option) => option.name}
                    value={allowedBranches.find(b => b._id === filters.branch) || null}
                    onChange={(event, newValue) => {
                        setFilters(f => ({ ...f, branch: newValue?._id || '', page: 1 }));
                    }}
                    disabled={!canSelectBranch}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    clearOnBlur={false}
                    blurOnSelect={true}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={`Branch (${allowedBranches.length})`}
                            sx={{ minWidth: 200 }}
                        />
                    )}
                    filterOptions={(options, { inputValue }) => {
                        if (inputValue === '') return options;
                        return options.filter(option =>
                            option.name.toLowerCase().includes(inputValue.toLowerCase())
                        );
                    }}
                />
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 5, ...containerProps }} dir={isRTL ? 'rtl' : 'ltr'}>
                {summaryData.map((item, index) => {
                    const IconComponent = item.icon;
                    return (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Card>
                                <CardContent>
                                    <Box sx={cardGridProps}>
                                        <Box sx={textContainerProps}>
                                            <Typography color="textSecondary" gutterBottom>
                                                {item.title}
                                            </Typography>
                                            <Typography variant="h5" component="div">
                                                {item.value}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ ...iconContainerProps, color: item.color }}>
                                            <IconComponent />
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </>
    );
};

export default SummaryCards;